import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BoltIcon from '@mui/icons-material/Bolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { LinearProgress, Paper } from '@mui/material';
import { DashboardData } from '../types';
import { kpiService, analyticsService, measurementService } from '../services/api';
import KPICard from '../components/KPICard';
import Gauge from '../components/Gauge';
import EfficiencyTrendChart from '../components/EfficiencyTrendChart';
import ProductionOverviewChart from '../components/ProductionOverviewChart';
import AlertPanel from '../components/AlertPanel';
import { colors, shadows, borderRadius } from '../theme/designSystem';
import { useWebSocket } from '../context/WebSocketContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [powerData, setPowerData] = useState<any>(null);
  const [sentryCCR, setSentryCCR] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { connected, latestKpi, latestMeasurement, latestDigesterStatus } = useWebSocket();

  useEffect(() => {
    loadDashboardData();
    // Refresh every 5 minutes (fallback)
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Update dashboard when new KPI data arrives via WebSocket
  useEffect(() => {
    if (latestKpi && dashboardData) {
      // Only update if we already have data (don't trigger on initial mount)
      loadDashboardData();
    }
  }, [latestKpi]);

  // Note: Removed measurement update trigger to reduce API calls
  // WebSocket KPI updates will keep data fresh

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load critical data first (dashboard KPIs)
      const data = await kpiService.getDashboardData();
      setDashboardData(data);
      
      // Load optional data with individual error handling
      try {
        const powerResult = await analyticsService.getHourlyPower();
        setPowerData(powerResult);
      } catch (err) {
        console.warn('Failed to load power data:', err);
      }
      
      try {
        const ccrData = await measurementService.getSentryCCR();
        setSentryCCR(ccrData);
      } catch (err) {
        console.warn('Failed to load Sentry CCR data:', err);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data. Please ensure the backend is running.');
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner on initial load
  if (loading && !dashboardData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress sx={{ color: colors.primary[600] }} />
      </Box>
    );
  }

  // Show error if data failed to load
  if (error && !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please check that the backend server is running and accessible.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const latest = dashboardData?.latest;
  // Find the most recent day with actual gas data for KPI summary
  // (today may show 0 early in the morning before gas starts flowing)
  const latestWithData = dashboardData?.timeSeries?.find(
    (kpi: any) => kpi && kpi.gas_produced_m3 > 0
  ) || latest;
  const kpiDateLabel = latestWithData?.date
    ? `As of ${new Date(latestWithData.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : '';

  return (
    <Box>
      {/* Connection Status */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Chip
          icon={connected ? <WifiIcon /> : <WifiOffIcon />}
          label={connected ? 'Real-time Connected' : 'Offline Mode'}
          color={connected ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Alert Panel */}
      <AlertPanel />

      {/* KPI Cards */}
      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        Key Performance Indicators
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
            title="Gas Yield Efficiency"
            value={latestWithData?.kpi_gas_yield_m3_per_kg || 0}
            unit="m³/kg"
            subtitle={kpiDateLabel || "Target: 0.3 - 0.6"}
            gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
            icon={<ShowChartIcon />}
            trend={12.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
            title="Energy Efficiency"
            value={latestWithData?.kpi_specific_energy_m3_per_kwh || 0}
            unit="m³/kWh"
            subtitle={kpiDateLabel || "Higher is better"}
            gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
            icon={<BoltIcon />}
            trend={5.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
            title="Daily Net Balance"
            value={latestWithData?.daily_net_balance || 0}
            unit="₱"
            subtitle={kpiDateLabel || "Revenue - Costs"}
            gradient={
              (latestWithData?.daily_net_balance || 0) >= 0
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            }
            icon={<AttachMoneyIcon />}
            trend={(latestWithData?.daily_net_balance || 0) >= 0 ? 8.2 : -3.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KPICard
            title="Gas Produced"
            value={latestWithData?.gas_produced_m3 || 0}
            unit="m³"
            subtitle={kpiDateLabel || "Total volume"}
            gradient="linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)"
            icon={<LocalGasStationIcon />}
            trend={4.7}
          />
        </Grid>
      </Grid>

      {/* Gauges */}
      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        Digester Temperatures
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Digester 1 Temp"
            value={latest?.digester_1_temp || 0}
            min={0}
            max={80}
            targetMin={30}
            targetMax={60}
            unit="°C"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Digester 2 Temp"
            value={latest?.digester_2_temp || 0}
            min={0}
            max={80}
            targetMin={30}
            targetMax={60}
            unit="°C"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Digester 3 Temp"
            value={latest?.digester_3_temp || 0}
            min={0}
            max={80}
            targetMin={30}
            targetMax={60}
            unit="°C"
          />
        </Grid>
      </Grid>

      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        Fermenter Fill Levels
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Fermenter 1 Level"
            value={latest?.fermenter_1_level || 0}
            min={0}
            max={100}
            targetMin={70}
            targetMax={95}
            unit="%"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Fermenter 2 Level"
            value={latest?.fermenter_2_level || 0}
            min={0}
            max={100}
            targetMin={70}
            targetMax={95}
            unit="%"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Gauge
            title="Fermenter 3 Level"
            value={latest?.fermenter_3_level || 0}
            min={0}
            max={100}
            targetMin={70}
            targetMax={95}
            unit="%"
          />
        </Grid>
      </Grid>

      {/* Sentry CCR */}
      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        Sentry CCR
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[700], mb: 1 }}>
              Sentry CCR 1
            </Typography>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: colors.primary[600] }}>
              {sentryCCR?.sentryccr1?.tagvalue ? Number(sentryCCR.sentryccr1.tagvalue).toFixed(2) : '--'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated: {sentryCCR?.sentryccr1?.datelog 
                ? new Date(sentryCCR.sentryccr1.datelog).toLocaleString() 
                : 'No data'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[700], mb: 1 }}>
              Sentry CCR 2
            </Typography>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: colors.primary[600] }}>
              {sentryCCR?.sentryccr2?.tagvalue ? Number(sentryCCR.sentryccr2.tagvalue).toFixed(2) : '--'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated: {sentryCCR?.sentryccr2?.datelog 
                ? new Date(sentryCCR.sentryccr2.datelog).toLocaleString() 
                : 'No data'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Temperature Status */}
      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        System Status
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: borderRadius.card, boxShadow: shadows.card }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ThermostatIcon sx={{ 
                fontSize: '2rem', 
                color: (latest?.avg_temp_c || 0) >= 30 && (latest?.avg_temp_c || 0) <= 60 
                  ? colors.status.success 
                  : colors.status.warning, 
                mr: 1 
              }} />
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900] }}>
                Average Temperature Status
              </Typography>
            </Box>
            <Typography sx={{ 
              fontSize: '2rem', 
              fontWeight: 700, 
              color: (latest?.avg_temp_c || 0) >= 30 && (latest?.avg_temp_c || 0) <= 60 
                ? colors.status.success 
                : colors.status.warning,
              mb: 1 
            }}>
              {Number(latest?.avg_temp_c || 0).toFixed(1)}°C
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: colors.neutral[600] }}>
              Optimal Range: 30-60°C
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(((latest?.avg_temp_c || 0) / 80) * 100, 100)}
              sx={{
                mt: 2,
                height: 8,
                borderRadius: 4,
                backgroundColor: colors.neutral[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: (latest?.avg_temp_c || 0) >= 30 && (latest?.avg_temp_c || 0) <= 60 
                    ? colors.status.success 
                    : colors.status.warning,
                  borderRadius: 4,
                },
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Typography 
        sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.neutral[900],
          mb: 2,
        }}
      >
        Trends & Analytics
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          {dashboardData?.timeSeries && (
            <EfficiencyTrendChart
              data={dashboardData.timeSeries}
              targetMin={dashboardData.kpiRanges.kpi_gas_yield_m3_per_kg.min}
              targetMax={dashboardData.kpiRanges.kpi_gas_yield_m3_per_kg.max}
            />
          )}
        </Grid>
        <Grid item xs={12} lg={6}>
          {dashboardData?.weeklyData && (
            <ProductionOverviewChart data={dashboardData.weeklyData} />
          )}
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              p: 3,
              borderRadius: borderRadius.card,
              boxShadow: shadows.card,
              backgroundColor: '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BoltIcon sx={{ fontSize: '1.5rem', color: colors.status.warning, mr: 1 }} />
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: colors.neutral[900] }}>
                Hourly Power Consumption - 48 Hours
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={powerData?.hourly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis 
                  dataKey="timestamp" 
                  stroke={colors.neutral[600]} 
                  fontSize={11}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getHours()}:00`;
                  }}
                />
                <YAxis stroke={colors.neutral[600]} fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: `1px solid ${colors.neutral[200]}`,
                    borderRadius: '8px',
                  }}
                  formatter={(value: any) => [`${Number(value).toFixed(1)} kWh`, 'Consumption']}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="kwh" 
                  fill={colors.status.warning} 
                  name="Power (kWh)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
